/**
 * LenderPortfolio — LIVE lender view (read-only).
 * Route: /lender
 *
 * Reads GET /api/lender/portfolio for the signed-in lender and shows every
 * borrower + their loans (projects) with draw status, INTEREST economics,
 * compliance (photos + signed lien waivers) and loan expirations.
 * Clicking a loan opens the real builder/developer project view (read-only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TourLauncher from '@/components/TourLauncher';
import {
  Landmark, Search, ChevronDown, ChevronRight, TrendingUp, Wallet, Clock, Percent,
  ShieldCheck, ShieldAlert, FileText, CheckCircle2, CircleDot, AlertTriangle,
  Building2, DollarSign, Loader2, Lock, ArrowRight,
  Inbox, CalendarClock, Check, X, Flame, Loader,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const moneyK = (n) => (Math.abs(n) >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round((n || 0) / 1000)}K`);

function BrandMark({ size = 34 }) {
  return (
    <div className="flex items-center justify-center font-extrabold text-white"
      style={{ width: size, height: size, background: '#2563EB', borderRadius: size * 0.26, fontSize: size * 0.52, lineHeight: 1 }}>D</div>
  );
}

export default function LenderPortfolio() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('portfolio'); // 'portfolio' | 'draws' | 'upcoming'
  const [draws, setDraws] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [acting, setActing] = useState(null); // draw request id currently being approved/rejected

  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);

  const loadDrawData = React.useCallback(() => {
    if (!user?.id) return;
    axios.get(`${API}/api/lender/draw-requests`, { params: { user_id: user.id } })
      .then((r) => setDraws(r.data)).catch(() => setDraws({ draw_requests: [], pending_count: 0, pending_amount: 0 }));
    axios.get(`${API}/api/lender/upcoming-draws`, { params: { user_id: user.id } })
      .then((r) => setUpcoming(r.data)).catch(() => setUpcoming({ upcoming_draws: [], total: 0 }));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { setError('Please sign in to view the lender portfolio.'); setLoading(false); return; }
    axios.get(`${API}/api/lender/portfolio`, { params: { user_id: user.id } })
      .then((r) => { setData(r.data); setOpen(r.data.borrowers?.[0] ? { [r.data.borrowers[0].name]: true } : {}); })
      .catch(() => setError('Could not load the portfolio. Please try again.'))
      .finally(() => setLoading(false));
    loadDrawData();
  }, [user?.id, loadDrawData]);

  const decideDraw = async (id, action, note = '') => {
    setActing(id);
    try {
      await axios.post(`${API}/api/lender/draw-requests/${id}/${action}`, { note }, { params: { user_id: user.id } });
      loadDrawData();
      // refresh portfolio totals since an approval moves money
      axios.get(`${API}/api/lender/portfolio`, { params: { user_id: user.id } }).then((r) => setData(r.data)).catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not update the draw request.');
    } finally {
      setActing(null);
    }
  };

  const borrowers = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.borrowers
      .map((b) => {
        let loans = b.loans;
        if (filter === 'expiring') loans = loans.filter((l) => l.days_to_maturity != null && l.days_to_maturity <= 60);
        if (filter === 'compliance') loans = loans.filter((l) => !l.compliance_ok);
        if (filter === 'overbudget') loans = loans.filter((l) => l.over_budget);
        return { ...b, loans };
      })
      .filter((b) => b.loans.length > 0)
      .filter((b) => (term ? b.name.toLowerCase().includes(term) : true));
  }, [data, q, filter]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (error) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500 text-sm" data-testid="lender-error">{error}</div>;
  }
  const t = data.totals;

  return (
    <div className="min-h-screen" style={{ background: '#f1f5f9' }} data-testid="lender-portfolio">
      {/* Header */}
      <header className="text-white" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center gap-3">
          <BrandMark size={36} />
          <div className="flex-1 min-w-0">
            <p className="font-extrabold leading-none truncate">DrawReady <span className="font-medium text-blue-200">· Lender</span></p>
            <p className="text-[11px] text-blue-200 mt-0.5">Portfolio overview</p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-[11px] text-blue-200">Signed in as</p>
            <p className="text-sm font-semibold truncate max-w-[220px]">{user?.name || 'Lender'}</p>
          </div>
          <TourLauncher tourKey={`lender-${view}`} userId={user?.id} className="text-blue-100 hover:text-white hover:bg-white/10" />
        </div>
      </header>

      {/* Read-only banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-2 flex items-center gap-2 text-[12px] text-amber-800 font-medium">
          <Lock className="w-3.5 h-3.5" /> Read-only lender view — you can explore every borrower and loan, but nothing can be edited.
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Wallet} label="Total committed" value={moneyK(t.committed)} sub={`${t.loans} loans · ${t.borrowers} borrowers`} tone={NAVY} />
          <Kpi icon={DollarSign} label="Drawn to date" value={moneyK(t.drawn)} sub={`${Math.round((t.drawn / t.committed) * 100) || 0}% · ${moneyK(t.remaining)} available`} tone={GREEN} />
          <Kpi icon={Percent} label="Wtd. rate · monthly int." value={`${t.weighted_rate}%`} sub={`${money(t.monthly_interest)}/mo · ${money(t.interest_accrued)} accrued`} tone="#0ea5e9" />
          <Kpi icon={Clock} label="Expiring ≤ 60 days" value={String(t.expiring_soon)} sub={`${t.compliance_gaps} compliance gaps · ${t.over_budget || 0} over budget`} tone="#f59e0b" alert />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto">
          {[
            ['portfolio', 'Portfolio', Building2, null],
            ['draws', 'Draws Requested', Inbox, draws?.pending_count || 0],
            ['upcoming', 'Upcoming Draws', CalendarClock, upcoming?.upcoming_draws?.length || 0],
          ].map(([k, label, Icon, badge]) => (
            <button key={k} onClick={() => setView(k)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition shrink-0 whitespace-nowrap ${view === k ? 'text-slate-900' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
              style={view === k ? { borderColor: NAVY } : {}}
              data-testid={`lender-tab-${k}`}>
              <Icon className="w-4 h-4" />
              {label}
              {badge > 0 && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{badge}</span>}
            </button>
          ))}
        </div>

        {view === 'portfolio' && (
        <>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] max-w-md relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search borrowers…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid="lender-search" />
          </div>
          {[['all', 'All'], ['expiring', 'Expiring soon'], ['compliance', 'Compliance gaps'], ['overbudget', 'Over budget']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition ${filter === k ? 'text-white' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              style={filter === k ? { background: NAVY, borderColor: NAVY } : {}} data-testid={`lender-filter-${k}`}>{l}</button>
          ))}
          <span className="ml-auto text-xs font-semibold text-slate-500">{t.ongoing} ongoing · {t.completed} completed</span>
        </div>

        {/* Borrowers */}
        <div className="space-y-3">
          {borrowers.map((b) => (
            <BorrowerCard key={b.name} b={b} open={!!open[b.name]}
              onToggle={() => setOpen((o) => ({ ...o, [b.name]: !o[b.name] }))}
              onOpenLoan={(id) => navigate(`/project/${id}`)} />
          ))}
          {borrowers.length === 0 && <div className="text-center text-slate-400 text-sm py-12">No borrowers match this filter.</div>}
        </div>
        </>
        )}

        {view === 'draws' && (
          <DrawsRequested draws={draws} acting={acting} onDecide={decideDraw} onOpenLoan={(id) => navigate(`/project/${id}`)} />
        )}
        {view === 'upcoming' && (
          <UpcomingDraws upcoming={upcoming} onOpenLoan={(id) => navigate(`/project/${id}`)} />
        )}
      </div>
    </div>
  );
}


function Kpi({ icon: Icon, label, value, sub, tone, alert }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 ${alert ? 'border-amber-200' : 'border-slate-200'}`} data-testid={`lender-kpi-${label}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tone}1a` }}><Icon className="w-4 h-4" style={{ color: tone }} /></div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      </div>
      <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{value}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

function BorrowerCard({ b, open, onToggle, onOpenLoan }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`lender-borrower-${b.name}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><Building2 className="w-4 h-4" style={{ color: NAVY }} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 truncate">{b.name}</p>
          <p className="text-[11px] text-slate-400">{b.loans.length} loans · {b.ongoing} ongoing · {b.completed} completed</p>
        </div>
        <div className="hidden md:flex items-center gap-6 text-right">
          <MiniStat label="Committed" value={moneyK(b.committed)} />
          <MiniStat label="Remaining" value={moneyK(b.remaining)} tone={GREEN} />
          <MiniStat label="Int./mo" value={moneyK(b.monthly_interest)} tone="#0ea5e9" />
          <div className="w-24">
            <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span>Drawn</span><span>{Math.round((b.drawn / b.committed) * 100) || 0}%</span></div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(b.drawn / b.committed) * 100 || 0}%`, background: NAVY }} /></div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {b.compliance_gaps > 0
            ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><ShieldAlert className="w-3 h-3" /> {b.compliance_gaps}</span>
            : <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3" /> OK</span>}
          {b.over_budget > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-200" title={`${b.over_budget} loan(s) over budget`}><Flame className="w-3 h-3" /> {b.over_budget}</span>}
          {b.expiring_soon > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-200"><Clock className="w-3 h-3" /> {b.expiring_soon}</span>}
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100">
          <div className="hidden lg:grid grid-cols-[1.6fr_0.8fr_1.2fr_0.9fr_0.7fr_1fr_0.8fr_0.7fr] gap-2 px-4 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <span>Loan / project</span><span>Status</span><span>Principal / drawn</span><span className="text-right">Remaining</span><span className="text-right">Rate</span><span className="text-right">Interest accrued</span><span className="text-center">Compliance</span><span className="text-right">Expires</span>
          </div>
          {b.loans.map((l) => (
            <button key={l.id} onClick={() => onOpenLoan(l.id)}
              className="w-full grid grid-cols-2 lg:grid-cols-[1.6fr_0.8fr_1.2fr_0.9fr_0.7fr_1fr_0.8fr_0.7fr] gap-2 items-center px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-blue-50/50 text-left group"
              data-testid={`lender-loan-${l.id}`}>
              <div className="min-w-0 col-span-2 lg:col-span-1">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-blue-800">{l.name}</p>
                  {l.over_budget && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200 flex-shrink-0" title={l.over_budget_flags?.map(f => `${f.name}: ${money(f.actual)} vs ${money(f.budget)} (+${f.over_pct}%)`).join('\n')}><Flame className="w-2.5 h-2.5" /> Over budget</span>}
                </div>
                <p className="text-[11px] text-slate-400 truncate">{l.address} · {l.draw_count} draws <ArrowRight className="w-3 h-3 inline opacity-0 group-hover:opacity-100 transition" /></p>
              </div>
              <div><StatusPill status={l.status} /></div>
              <div>
                <p className="text-sm font-semibold text-slate-800 tabular-nums">{money(l.principal)}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${l.drawn_pct}%`, background: l.status === 'completed' ? GREEN : NAVY }} /></div>
                  <span className="text-[10px] text-slate-400 w-8 text-right">{l.drawn_pct}%</span>
                </div>
              </div>
              <p className="text-sm font-bold tabular-nums text-right" style={{ color: l.remaining > 0 ? GREEN : '#94a3b8' }}>{money(l.remaining)}</p>
              <p className="text-sm font-semibold tabular-nums text-right text-slate-700">{l.interest_rate}%</p>
              <p className="text-sm font-semibold tabular-nums text-right text-sky-700">{money(l.interest_accrued)}</p>
              <div className="flex items-center justify-center gap-1">
                <DocDot ok={!l.photo_gap} label="Photos" />
                <DocDot ok={!l.lien_waiver_gap} label="Waiver" />
              </div>
              <div className="text-right">
                {l.days_to_maturity == null ? <span className="text-[11px] text-slate-400">—</span>
                  : <span className={`text-[11px] font-semibold ${l.days_to_maturity <= 60 ? 'text-red-600' : 'text-slate-500'}`}>{l.days_to_maturity}d</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return <div><p className="text-[10px] text-slate-400">{label}</p><p className="text-sm font-bold tabular-nums" style={{ color: tone || '#0f172a' }}>{value}</p></div>;
}
function StatusPill({ status }) {
  return status === 'completed'
    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200"><CheckCircle2 className="w-3 h-3" /> Completed</span>
    : <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><CircleDot className="w-3 h-3" /> Ongoing</span>;
}
function DocDot({ ok, label }) {
  return <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`} title={ok ? `${label} on file` : `${label} missing`}>{ok ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {label}</span>;
}

function DrawsRequested({ draws, acting, onDecide, onOpenLoan }) {
  const items = draws?.draw_requests || [];
  const pending = items.filter((d) => d.status === 'pending');
  const decided = items.filter((d) => d.status !== 'pending');
  return (
    <div className="space-y-4" data-testid="lender-draws-requested">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900">{draws?.pending_count || 0} draw request{(draws?.pending_count || 0) === 1 ? '' : 's'} awaiting your decision</p>
          <p className="text-[12px] text-slate-500 mt-0.5">Approving a request auto-records the draw and updates the borrower&rsquo;s available funds.</p>
        </div>
        <span className="text-lg font-extrabold tabular-nums" style={{ color: NAVY }}>{money(draws?.pending_amount || 0)}</span>
      </div>

      {pending.length === 0 && (
        <div className="text-center text-slate-400 text-sm py-10 rounded-2xl border border-dashed border-slate-200 bg-white" data-testid="lender-draws-empty">
          No draw requests pending. When a borrower submits one in-app, it lands here for approval.
        </div>
      )}

      {pending.map((d) => (
        <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`draw-request-${d.id}`}>
          <div className="flex items-start justify-between gap-3">
            <button onClick={() => onOpenLoan(d.project_id)} className="text-left min-w-0 group">
              <p className="text-sm font-bold text-slate-900 truncate group-hover:text-blue-800">{d.project_name}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Requested {new Date(d.requested_at).toLocaleDateString()}</p>
              {d.notes && <p className="text-[12px] text-slate-600 mt-1">{d.notes}</p>}
            </button>
            <span className="text-lg font-extrabold tabular-nums flex-shrink-0" style={{ color: GREEN }}>{money(d.amount)}</span>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button disabled={acting === d.id} onClick={() => onDecide(d.id, 'approve')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              data-testid={`draw-approve-${d.id}`}>
              {acting === d.id ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve & record
            </button>
            <button disabled={acting === d.id} onClick={() => { const note = window.prompt('Reason for rejecting (optional):') ?? ''; onDecide(d.id, 'reject', note); }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`draw-reject-${d.id}`}>
              <X className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>
      ))}

      {decided.length > 0 && (
        <div className="pt-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Recent decisions</p>
          <div className="space-y-2">
            {decided.slice(0, 12).map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <span className="font-semibold text-slate-700 truncate">{d.project_name}</span>
                  {d.decision_note && <span className="text-[11px] text-slate-400 ml-2">“{d.decision_note}”</span>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="tabular-nums text-slate-500">{money(d.amount)}</span>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${d.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {d.status === 'approved' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {d.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UpcomingDraws({ upcoming, onOpenLoan }) {
  const items = upcoming?.upcoming_draws || [];
  // Group by project so the bank sees ~one row per loan, not hundreds of phases.
  const groups = React.useMemo(() => {
    const m = {};
    items.forEach((s) => {
      const g = m[s.project_id] || (m[s.project_id] = { project_id: s.project_id, project_name: s.project_name, borrower: s.borrower, total: 0, phases: [] });
      g.total += s.suggested_amount;
      g.phases.push(s);
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [items]);
  const [openG, setOpenG] = useState({});

  return (
    <div className="space-y-4" data-testid="lender-upcoming-draws">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900">{groups.length} loan{groups.length === 1 ? '' : 's'} with anticipated draws · {items.length} phases</p>
          <p className="text-[12px] text-slate-500 mt-0.5">Suggested from phases that are complete or have materials purchased but are not fully drawn yet.</p>
        </div>
        <span className="text-lg font-extrabold tabular-nums" style={{ color: NAVY }}>{money(upcoming?.total || 0)}</span>
      </div>
      {groups.length === 0 && (
        <div className="text-center text-slate-400 text-sm py-10 rounded-2xl border border-dashed border-slate-200 bg-white">
          Nothing anticipated yet — as phases are completed or materials are purchased, likely next draws show up here.
        </div>
      )}
      {groups.map((g) => (
        <div key={g.project_id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="w-full flex items-center justify-between gap-3 px-4 py-3">
            <button onClick={() => setOpenG((o) => ({ ...o, [g.project_id]: !o[g.project_id] }))}
              className="flex items-center gap-2 min-w-0 text-left" data-testid={`upcoming-group-${g.project_id}`}>
              {openG[g.project_id] ? <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{g.project_name}</p>
                <p className="text-[11px] text-slate-400">{g.borrower} · {g.phases.length} phase{g.phases.length === 1 ? '' : 's'}</p>
              </div>
            </button>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-base font-extrabold tabular-nums" style={{ color: GREEN }}>{money(g.total)}</span>
              <button onClick={() => onOpenLoan(g.project_id)} className="text-[12px] font-semibold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">Open <ArrowRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {openG[g.project_id] && (
            <div className="border-t border-slate-100 divide-y divide-slate-50">
              {g.phases.map((s, i) => (
                <div key={`${s.phase_id}-${i}`} className="flex items-center justify-between px-4 py-2 pl-10 text-sm" data-testid={`upcoming-draw-${s.phase_id}`}>
                  <div className="min-w-0">
                    <span className="font-medium text-slate-700 truncate">{s.phase_name}</span>
                    <span className="text-[11px] text-slate-400 ml-2">{s.reason}</span>
                  </div>
                  <span className="tabular-nums font-semibold text-slate-600">{money(s.suggested_amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

